import { useEffect, useRef, useMemo } from 'react';

// Hooks
import { useQuery } from '@apollo/react-hooks';
import { NetworkStatus } from 'apollo-client';
import { useInfringementDataTable } from 'src/@infringements/reducers';

// Types
import {
  GetInfringementCandidatesQuery,
  GetInfringementCandidatesQueryVariables,
  GetInfringementsQuery,
  GetInfringementsQueryVariables,
} from 'src/app/gql/__generated__/hooks';
// Queries
import { GET_INFRINGEMENTS, GET_INFRINGEMENT_CANDIDATES } from 'src/app/gql/queries/infringements';

// Helpers
import { dateRangeStore } from 'src/app/stores/dateRangeStore';
import { dateTimeInQuery } from 'src/utils';
import { dataActionTypes as infringementActionTypes } from 'src/@infringements/newActionTypes';
import { dataActionTypes as infringementCanActionTypes } from 'src/@infringementsCandidates/newActionTypes';

// Extract the item types from the queries
type InfringementItem = NonNullable<
  NonNullable<GetInfringementsQuery['infringementSummariesByTimeRange']>['items']
>[0];
type CandidateItem = NonNullable<
  NonNullable<GetInfringementCandidatesQuery['infringementCandidateSummariesByTimeRange']>['items']
>[0];

// Normalized return type that extracts the common connection structure
type NormalizedInfringementData = {
  items: Array<InfringementItem | CandidateItem>;
  totalCount: number | null;
  pageInfo: {
    startCursor?: string | null;
    endCursor?: string | null;
    hasNextPage: boolean;
  };
} | null;

export enum InfringementType {
  REGULAR = 'regular',
  CANDIDATE = 'candidate',
}

export const useGetInfringements = (
  dispatcher: any,
  type: InfringementType = InfringementType.REGULAR
) => {
  const { from, to } = dateRangeStore.getDateFilters();
  const {
    state: { variables, hasCandidatesEnabled },
  } = useInfringementDataTable();

  // Common query variables to avoid duplication
  const queryVariables = useMemo(
    () => ({
      ...(variables as GetInfringementsQueryVariables),
      startTime: dateTimeInQuery(from, 'start'),
      endTime: dateTimeInQuery(to, 'end'),
    }),
    [variables, from, to]
  );

  // Regular infringements query
  const {
    data: infringementsData,
    loading: infringementsLoading,
    error: infringementsError,
    refetch: refetchInfringements,
    fetchMore: fetchMoreInfringements,
    networkStatus: infringementsNetworkStatus,
  } = useQuery<GetInfringementsQuery, GetInfringementsQueryVariables>(GET_INFRINGEMENTS, {
    variables: queryVariables,
    skip: hasCandidatesEnabled,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onError: error => {
      console.error('Error fetching infringements:', error);
    },
  });

  // Candidates query
  const {
    data: candidatesData,
    loading: candidatesLoading,
    error: candidatesError,
    refetch: refetchCandidates,
    fetchMore: fetchMoreCandidates,
    networkStatus: candidatesNetworkStatus,
  } = useQuery<GetInfringementCandidatesQuery, GetInfringementCandidatesQueryVariables>(
    GET_INFRINGEMENT_CANDIDATES,
    {
      variables: queryVariables,
      skip: !hasCandidatesEnabled,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      onError: error => {
        console.error('Error fetching infringement candidates:', error);
      },
    }
  );
  // const dispatcher = useContext(InfringementDispatchContext);

  // Normalize the data structure
  const normalizedData: NormalizedInfringementData = useMemo(() => {
    if (hasCandidatesEnabled && candidatesData) {
      const connection = candidatesData.infringementCandidateSummariesByTimeRange;
      if (!connection) {
        return null;
      }

      let actionType = infringementActionTypes;
      if (type === InfringementType.CANDIDATE) {
        actionType = infringementCanActionTypes;
      }

      dispatcher({
        type: actionType.RESET_DATA,
      });
      dispatcher({
        type: actionType.INFRINGEMENTS_FETCHED,
        data: {
          data: connection.items || [],
          selectedDateRange: {
            from: queryVariables.startTime,
            to: queryVariables.endTime,
          },
          pageInfo: connection.pageInfo,
        },
        candidates: hasCandidatesEnabled, // Indicate this is not candidates data
      });

      return {
        items: connection.items || [],
        totalCount: connection.totalCount || null,
        pageInfo: connection.pageInfo,
      };
    }

    if (!hasCandidatesEnabled && infringementsData) {
      const connection = infringementsData.infringementSummariesByTimeRange;
      if (!connection) {
        return null;
      }

      let actionType = infringementActionTypes;
      if (type === InfringementType.CANDIDATE) {
        actionType = infringementCanActionTypes;
      }

      dispatcher({
        type: actionType.RESET_DATA,
      });
      dispatcher({
        type: actionType.INFRINGEMENTS_FETCHED,
        data: {
          data: connection.items || [],
          selectedDateRange: {
            from: queryVariables.startTime,
            to: queryVariables.endTime,
          },
          pageInfo: connection.pageInfo,
        },
        candidates: hasCandidatesEnabled, // Indicate this is not candidates data
      });

      return {
        items: connection.items || [],
        totalCount: connection.totalCount || null,
        pageInfo: connection.pageInfo,
      };
    }

    return null;
  }, [hasCandidatesEnabled, candidatesData, infringementsData]);

  // Compute derived state
  const loading = hasCandidatesEnabled ? candidatesLoading : infringementsLoading;
  const error = hasCandidatesEnabled ? candidatesError : infringementsError;
  const refetch = hasCandidatesEnabled ? refetchCandidates : refetchInfringements;
  const fetchMore = hasCandidatesEnabled ? fetchMoreCandidates : fetchMoreInfringements;
  const loadingMore = hasCandidatesEnabled
    ? candidatesNetworkStatus === NetworkStatus.fetchMore
    : infringementsNetworkStatus === NetworkStatus.fetchMore;

  return {
    data: normalizedData,
    loading,
    loadingMore,
    error,
    refetch,
    fetchMore,
    // Optional: expose individual query states for more granular control
    infringements: {
      data: infringementsData,
      loading: infringementsLoading,
      error: infringementsError,
      refetch: refetchInfringements,
    },
    candidates: {
      data: candidatesData,
      loading: candidatesLoading,
      error: candidatesError,
      refetch: refetchCandidates,
    },
  };
};

export const useGetInfringementsOLD = () => {
  const { from, to } = dateRangeStore.getDateFilters();
  const {
    state: { variables },
  } = useInfringementDataTable();
  // Track refetch attempts with a ref to persist across renders
  const refetchAttempts = useRef(0);

  const queryResult = useQuery<GetInfringementsQuery, GetInfringementsQueryVariables>(
    GET_INFRINGEMENTS,
    {
      variables: {
        ...(variables as GetInfringementsQueryVariables),
        startTime: dateTimeInQuery(from, 'start'),
        endTime: dateTimeInQuery(to, 'end'),
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  const { data, refetch } = queryResult;

  // Reset refetch counter when query variables change (new search)
  useEffect(() => {
    refetchAttempts.current = 0;
  }, [variables, from, to]);

  // Auto-refetch if totalCount is -1 and we're not already loading
  useEffect(() => {
    if (
      data?.infringementSummariesByTimeRange?.totalCount === -1 &&
      !queryResult.loading &&
      refetchAttempts.current < 5 // Safety: max 2 refetch attempts
    ) {
      // Small delay to avoid immediate refetch
      const timer = setTimeout(() => {
        refetchAttempts.current += 1;

        // Use the startCursor from the current data to maintain cache connection
        const refetchVariables = {
          ...variables,
          startTime: dateTimeInQuery(from, 'start'),
          endTime: dateTimeInQuery(to, 'end'),
          after: data.infringementSummariesByTimeRange.pageInfo.startCursor,
        };

        void refetch(refetchVariables);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [
    data?.infringementSummariesByTimeRange?.totalCount,
    queryResult.loading,
    refetch,
    variables,
    from,
    to,
    data?.infringementSummariesByTimeRange?.pageInfo?.startCursor,
  ]);

  return queryResult;
};
